import classNames from 'classnames';
import {observer, Provider} from 'mobx-react';
import React, {useCallback, useEffect, useState, useMemo, useRef, useReducer} from 'react';

import {
    AsyncPriceCourseCardContext,
    BrowseCourseBadgesContext,
    COURSE_BADGE_PREMIUM,
} from '@udemy/browse-course';
import {
    PAGE_TYPE_CATEGORY,
    PAGE_TYPE_ORG_CATEGORY,
    PAGE_TYPE_ORG_SUBCATEGORY,
    PAGE_TYPE_SUBCATEGORY,
    PAGE_TYPE_SUBS_CATEGORY,
    PAGE_TYPE_SUBS_SUBCATEGORY,
    DiscoveryUnit,
    DiscoveryUnitItem,
} from '@udemy/discovery-api';
import {Tracker} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {
    CourseUnitSkeletonVerticalLayout,
    DiscoveryUnitsContainer,
    DiscoveryUnitsLoadingSkeleton,
} from '@udemy/react-discovery-units';
import {ExperimentImpressionEvent, useFeatures} from '@udemy/react-experimentation-provider';
import {ContentArea} from '@udemy/react-layout-components';
import {SubcategoryLinkBar, SubcategoryLinkBarLoader, CategoryShape} from '@udemy/react-link-bar';
import {ResourceContextMenuProvider} from '@udemy/resource-context-menu';
import {useConsumerSubscriptionPlan} from '@udemy/subscription-graphql';
import {UDData, useUDData, useUpdateUDData} from '@udemy/ud-data';

import {VernacularCohortBrowseBanner} from 'src/components/vernacular-cohort-browse-banner/vernacular-cohort-browse-banner.react-component';
import {
    QUICK_VIEW_BOX_FEATURE,
    TOPIC_CAT_SUBCAT_MWEB_CARD_STANDARDIZATION,
    VALUE_PLANSTARTER_PLAN,
} from 'src/lib/experimentation-constants';
import {
    getTopicCatSubcatMwebCardStandardizationConfiguration,
    isMwebStandardizedMobileCourseCardsEnabled,
} from 'src/lib/experimentation-helper';

import {fetchCategoryDataFromGql} from '../../pages/courses/lib/get-category-data';
import {parseCategoryData} from '../../pages/courses/lib/parse-category-data';
import styles from './category.module.less';
import {DiscoveryListContainer} from './components/browse/discovery-list-container/discovery-list-container.react-component';
import {RefundNotice} from './components/browse/notices/refund-notice.react-component';
import {
    fetchDiscoveryUnits,
    getDiscoveryUnitComponentAndPropsFallback,
} from './lib/discovery-units-component-and-props';

export interface CategoryProps {
    pageObject: {
        id: number;
        category_id?: number;
        title_cleaned: string;
        title: string;
        type: typeof PAGE_TYPE_CATEGORY | typeof PAGE_TYPE_SUBCATEGORY;
    };
    staticCategory?: CategoryShape;
    subsCollectionIds?: string;
    showCodingExercisesBadge?: boolean;
    staticDiscoveryUnits?: DiscoveryUnit[];
}

export const CategoryPage: React.FC<CategoryProps> = observer(
    ({
        pageObject,
        staticCategory,
        showCodingExercisesBadge,
        staticDiscoveryUnits = [],
        subsCollectionIds,
    }) => {
        const {gettext, interpolate, locale} = useI18n();
        const udData = useUDData();
        const {Config: udConfig} = udData;
        const hasOrganization = udConfig.brand.has_organization;
        // Replace multiple loading states with a single reducer
        interface UnitsState {
            units: DiscoveryUnit[];
            isSubscriptionPlanLoading: boolean;
            isDiscoveryUnitsLoaded: boolean;
            isFetchingUnits: boolean;
        }

        type UnitsAction =
            | {type: 'SUBSCRIPTION_PLAN_LOADED'}
            | {type: 'FETCH_UNITS_START'}
            | {type: 'FETCH_UNITS_SUCCESS'; units: DiscoveryUnit[]}
            | {type: 'RENDER_STATIC_UNITS'; units: DiscoveryUnit[]};

        const unitsReducer = (state: UnitsState, action: UnitsAction): UnitsState => {
            switch (action.type) {
                case 'SUBSCRIPTION_PLAN_LOADED':
                    return {...state, isSubscriptionPlanLoading: false};
                case 'FETCH_UNITS_START':
                    return {...state, isFetchingUnits: true};
                case 'FETCH_UNITS_SUCCESS':
                    return {
                        ...state,
                        units: action.units,
                        isDiscoveryUnitsLoaded: true,
                        isFetchingUnits: false,
                    };
                case 'RENDER_STATIC_UNITS':
                    return {
                        ...state,
                        units: action.units,
                        isDiscoveryUnitsLoaded: true,
                    };
                default:
                    return state;
            }
        };

        const initialUnitsState: UnitsState = {
            units: [],
            isSubscriptionPlanLoading: true,
            isDiscoveryUnitsLoaded: false,
            isFetchingUnits: false,
        };

        const [categoryState, setCategoryState] = useState({
            category: staticCategory,
            isLoading: !staticCategory || Object.keys(staticCategory).length === 0,
        });
        const [unitsState, dispatchUnitsAction] = useReducer(unitsReducer, initialUnitsState);
        const {units, isSubscriptionPlanLoading, isDiscoveryUnitsLoaded, isFetchingUnits} =
            unitsState;

        const consumerSubscriptionPlan = useConsumerSubscriptionPlan();

        useEffect(() => {
            // In some cases, a client-side call to retrieve cat/subcat from GQL may succeed where the server-side call failed.
            // For example, the server-side call is not associated with an organization, so if a category is in the UB collection
            // but not in the MX collection, then the server-side call will return nothing, but a client-side call (if associated with
            // an organization) will return something.
            //
            // So, if the server-side category doesn't exist, try to retrieve it client-side.
            const fetchCategoryFromGql = async () => {
                const slug = pageObject.category_id ?? pageObject.title_cleaned;
                fetchCategoryDataFromGql(
                    pageObject.category_id?.toString() ?? pageObject.title_cleaned,
                    locale,
                ).then((categoryData) => {
                    if (categoryData) {
                        setCategoryState({
                            category: parseCategoryData(
                                categoryData,
                                `Unable to parse categoryData with slug: ${slug} in component.`,
                            ),
                            isLoading: false,
                        });
                    } else {
                        setCategoryState({category: undefined, isLoading: false});
                    }
                });
            };

            if (!staticCategory || Object.keys(staticCategory).length === 0) {
                fetchCategoryFromGql();
            }
        }, [staticCategory, pageObject.title_cleaned, pageObject.category_id, locale]);

        useEffect(() => {
            if (hasOrganization) {
                dispatchUnitsAction({type: 'SUBSCRIPTION_PLAN_LOADED'});
            } else if (!consumerSubscriptionPlan?.isLoading) {
                dispatchUnitsAction({type: 'SUBSCRIPTION_PLAN_LOADED'});
            }
        }, [consumerSubscriptionPlan?.isLoading, hasOrganization]);

        const isConsumerSubsSubscriber = !!(
            !consumerSubscriptionPlan?.isLoading && consumerSubscriptionPlan?.isSubscriber
        );

        // Externalize the loading of discovery units outside the DiscoveryUnitsContainer / DiscoveryUnitsContainerStore
        // to enable us to fetch units during ISR for MX users and re-fetch units when the user is a subscriber / UB user
        const ref: React.RefObject<HTMLDivElement> = React.createRef();
        const mobileMaxMedia = useMatchMedia('mobile-max');
        const effectiveIsMobile =
            mobileMaxMedia != null ? mobileMaxMedia : Boolean(udData.request?.isMobile);
        const deviceType = effectiveIsMobile ? 'mobile' : 'desktop';
        const {featureVariantAssignments: features} = useFeatures([
            QUICK_VIEW_BOX_FEATURE,
            TOPIC_CAT_SUBCAT_MWEB_CARD_STANDARDIZATION,
            VALUE_PLANSTARTER_PLAN,
        ]);
        const topicCatMwebCardStandardizationConfig =
            getTopicCatSubcatMwebCardStandardizationConfiguration(features);

        // TODO: GR-56 Remove after topic_cat_subcat_mweb_card_standardization concludes
        const showTopicCatSubcatMwebCardStandardization =
            isMwebStandardizedMobileCourseCardsEnabled(topicCatMwebCardStandardizationConfig);
        const showThreeCardVerticalLayout =
            effectiveIsMobile && showTopicCatSubcatMwebCardStandardization;

        const isValueStarterPlanEnabled =
            features.get(VALUE_PLANSTARTER_PLAN)?.configuration?.enableValuePlan ?? false;

        const pageType = useMemo(() => {
            const type = pageObject.type;
            if (type === 'category') {
                if (isConsumerSubsSubscriber) {
                    return PAGE_TYPE_SUBS_CATEGORY;
                }
                return hasOrganization ? PAGE_TYPE_ORG_CATEGORY : PAGE_TYPE_CATEGORY;
            } else if (type === 'subcategory') {
                if (isConsumerSubsSubscriber) {
                    return PAGE_TYPE_SUBS_SUBCATEGORY;
                }
                return hasOrganization ? PAGE_TYPE_ORG_SUBCATEGORY : PAGE_TYPE_SUBCATEGORY;
            }
            return undefined;
        }, [pageObject.type, isConsumerSubsSubscriber, hasOrganization]);

        // Determine if we need to fetch dynamic units - only fetch if the user is a subscriber, org user, or no server-side
        // static units were fetched
        const shouldFetchDynamicUnits = useMemo(() => {
            return (
                (hasOrganization || isConsumerSubsSubscriber || !staticDiscoveryUnits.length) &&
                pageType !== undefined
            );
        }, [hasOrganization, isConsumerSubsSubscriber, pageType, staticDiscoveryUnits.length]);

        const fetchUnits = useCallback(async () => {
            if (pageType) {
                try {
                    dispatchUnitsAction({type: 'FETCH_UNITS_START'});
                    const fetchedUnits = await fetchDiscoveryUnits({
                        udData,
                        pageType: pageType,
                        pageObjectId: pageObject.id,
                    });
                    const validUnits = fetchedUnits.results.filter(
                        (unit: DiscoveryUnit) => !unit.items || unit.items.length > 0,
                    );
                    dispatchUnitsAction({type: 'FETCH_UNITS_SUCCESS', units: validUnits});
                } catch (error) {
                    dispatchUnitsAction({type: 'FETCH_UNITS_SUCCESS', units: []});
                }
            }
        }, [pageObject.id, pageType, udData]);

        useEffect(() => {
            if (
                udData.isGlobalMeContextLoading ||
                consumerSubscriptionPlan?.isLoading ||
                isDiscoveryUnitsLoaded ||
                isFetchingUnits
            ) {
                return;
            }

            // Once subscription status and organization status are resolved, determine whether to re-fetch units
            if (shouldFetchDynamicUnits) {
                // For org users or subscribers, fetch dynamic units
                fetchUnits();
            } else if (!isSubscriptionPlanLoading) {
                // If no static units are provided and we don't need to fetch dynamic units,
                // set an empty array to avoid infinite loading
                const validStaticUnits = staticDiscoveryUnits.filter(
                    (unit: DiscoveryUnit) => !unit.items || unit.items.length > 0,
                );
                dispatchUnitsAction({type: 'RENDER_STATIC_UNITS', units: validStaticUnits});
            }
        }, [
            consumerSubscriptionPlan?.isLoading,
            fetchUnits,
            shouldFetchDynamicUnits,
            staticDiscoveryUnits,
            isDiscoveryUnitsLoaded,
            isFetchingUnits,
            isSubscriptionPlanLoading,
            udData.isGlobalMeContextLoading,
        ]);

        const updateUDData = useUpdateUDData();
        const updateUDDataRef = useRef(updateUDData);

        useEffect(() => {
            if (pageObject.id) {
                updateUDDataRef.current({
                    pageContext: {
                        entityId: pageObject.id.toString(),
                    },
                } as UDData);
            }
        }, [pageObject.id]);

        const handlePageChange = () => {
            ref.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
        };

        const getUnitComponentAndProps = (unit: Partial<DiscoveryUnit<DiscoveryUnitItem>>) => {
            const allCoursesInUserSubscription = () => {
                if (unit.item_type === 'course') {
                    return unit.items?.every(
                        (item: DiscoveryUnitItem & {is_in_user_subscription?: boolean}) =>
                            item?.is_in_user_subscription,
                    );
                }
                return false;
            };
            const defaultBrowseCarouselGridProps = {
                gridCols: !effectiveIsMobile ? 4 : undefined,
                renderExperimentalCourseCards: false,
            };

            const singleCourseUnitExperimentProps = {
                allowAddToCartSuccessModal: false,
                showAddToCartInCourseCard: true,
                showQuickViewBox: false,
                disableMobileAddToCartRedirect: true,
                cartButtonTextGoToCart: gettext('In your cart'),
                cartButtonDisableOnGoToCart: true,
            };
            const stackedMobileLayoutExperimentProps = {
                layout: 'singlecol',
                disableInfiniteScroll: true,
                showInstructorDetails: true,
                maxCards: 12,
                maxCardsPerStack: 3,
                ...singleCourseUnitExperimentProps,
                showAddToCartInCourseCard: !allCoursesInUserSubscription,
            };
            const threeCardVerticalLayoutProps = {
                layout: 'singlecol' as const,
                showStackedProductCardLayout: true,
                disableInfiniteScroll: true,
                showAddToCartInCourseCard: false,
                addToCartButtonStyle: 'primary' as const,
                addToCartButtonSize: 'xsmall' as const,
                courseCardProps: {
                    layout: 'vertical',
                    showCourseTag: false,
                    showDescription: false,
                    showDetails: false,
                    showRibbonsLast: true,
                    disablePremiumHighlightedRibbon: true,
                },
                showInstructorDetails: true,
                isMobileMax: effectiveIsMobile,
                showQuickViewBox: false,
                maxCardsPerStack: 3,
                maxCards: 3,
                showDividers: false,
                loadingSkeleton: <CourseUnitSkeletonVerticalLayout />,
            };

            const overridesUnitProps = {
                /* eslint-disable @typescript-eslint/naming-convention */
                PopularTopicsUnit: {
                    titleTypography: effectiveIsMobile ? 'ud-heading-lg' : undefined,
                },
                PopularInstructorsUnit: {
                    alternateHeadline: {
                        title: unit.title,
                        secondaryText: gettext(
                            'These real-world experts are highly rated by learners like you.',
                        ),
                        secondaryTextClass: styles['heading-secondary-subdued'],
                    },
                    showTitle: false,
                },
                CourseUnit: {
                    fullWidth: effectiveIsMobile,
                    showPager: !effectiveIsMobile,
                    layout: 'singlerow',
                    titleTypography: effectiveIsMobile ? 'ud-heading-lg' : undefined,
                    carouselProps: {
                        ariaLabel: unit.title,
                        ariaRoleDescription: gettext('carousel'),
                    },
                    ...{...defaultBrowseCarouselGridProps},
                    ...(effectiveIsMobile && stackedMobileLayoutExperimentProps),
                    ...(showThreeCardVerticalLayout && threeCardVerticalLayoutProps),
                },
                SingleCourseUnit: {
                    titleTypography: effectiveIsMobile ? 'ud-heading-lg' : undefined,
                    alternateHeadline: {
                        title: unit.title,
                        secondaryText: gettext(
                            'Many learners enjoyed this highly rated course for its engaging content.',
                        ),
                        secondaryTextClass: styles['heading-secondary-subdued'],
                    },
                    showTitle: false,
                    ...(effectiveIsMobile && singleCourseUnitExperimentProps),
                    ...(showThreeCardVerticalLayout && threeCardVerticalLayoutProps),
                },
                SkillsHubUnit: {
                    ...{...defaultBrowseCarouselGridProps},
                    showTitle: false,
                    // Shipped variant from mx_mobile_vertical_reco_units: always non-compact on mobile
                    compact: false,
                    alternateHeadline: {
                        title: gettext('Courses to get you started'),
                        titleClass: effectiveIsMobile ? 'ud-heading-lg' : 'ud-heading-xl',
                        secondaryText: gettext(
                            'Explore courses from experienced, real-world experts.',
                        ),
                        secondaryTextClass: styles['heading-secondary-subdued'],
                    },
                    className: 'component-margin',
                    ...(effectiveIsMobile && stackedMobileLayoutExperimentProps),
                    // Shipped variant from mx_mobile_vertical_reco_units: always applied on mobile
                    ...(effectiveIsMobile && threeCardVerticalLayoutProps),
                },
                /* eslint-enable @typescript-eslint/naming-convention */
            };
            return getDiscoveryUnitComponentAndPropsFallback(unit, overridesUnitProps);
        };

        const headerText = interpolate(
            gettext('%(categoryName)s Courses'),
            {categoryName: pageObject.title},
            true,
        );

        const verticalMobileBrowseLayoutExperimentProps = {
            showAddToCartInCourseCard: true,
            disableMobileAddToCartRedirect: true,
            cartButtonTextGoToCart: gettext('In your cart'),
            cartButtonDisableOnGoToCart: true,
            allowAddToCartSuccessModal: false,
        };

        const qvbFeature = features.get(QUICK_VIEW_BOX_FEATURE);
        const fullQuickViewBoxFeature = qvbFeature?.configuration?.feature ?? false;

        /* The experiment impression event will  trigger when feature is true */
        if (qvbFeature) {
            const {experimentIds = [], isLocalDefault = false} = qvbFeature;
            Tracker.publishEvent(new ExperimentImpressionEvent(experimentIds, isLocalDefault));
        }

        const browseCourseContextProps = {
            browseBadgeHandlerOptions: {
                ...(showThreeCardVerticalLayout && {
                    priority: {[COURSE_BADGE_PREMIUM]: Number.MAX_SAFE_INTEGER},
                }),
            },
        };

        const asyncPriceBrowseCardContextValue = showThreeCardVerticalLayout
            ? {
                  useCourseProductCard: true,
                  hideCourseDescription: true,
              }
            : {};

        const discoveryAndDirectorySection = (
            <>
                {!isDiscoveryUnitsLoaded || isFetchingUnits ? (
                    <DiscoveryUnitsLoadingSkeleton className="component-margin" />
                ) : (
                    <DiscoveryUnitsContainer
                        className={'component-margin'}
                        pageType={pageType}
                        pageObjectId={pageObject.id}
                        deviceType={deviceType}
                        itemCount={6}
                        getUnitComponentAndProps={getUnitComponentAndProps}
                        units={units}
                    />
                )}
                <div className="component-margin" ref={ref}>
                    <h2
                        className={classNames(
                            effectiveIsMobile ? 'ud-heading-lg' : 'ud-heading-xl',
                            styles['heading-secondary'],
                        )}
                    >
                        {interpolate(
                            gettext('All %(categoryTitle)s courses'),
                            {categoryTitle: pageObject.title},
                            true,
                        )}
                    </h2>
                    {!isConsumerSubsSubscriber && (
                        <RefundNotice className={styles['refund-notice']} />
                    )}
                    <DiscoveryListContainer
                        pageType={pageType}
                        pageSize={16}
                        pageObjectId={pageObject.id}
                        onPageChange={handlePageChange}
                        subsCollectionIds={subsCollectionIds}
                        query={pageObject.title}
                        fullQuickView={fullQuickViewBoxFeature}
                        isValueStarterPlanEnabled={isValueStarterPlanEnabled}
                        {...(effectiveIsMobile
                            ? {
                                  ...verticalMobileBrowseLayoutExperimentProps,
                                  ...(showTopicCatSubcatMwebCardStandardization && {
                                      showAddToCartInCourseCard: false,
                                  }),
                              }
                            : {})}
                    />
                </div>
            </>
        );

        return (
            <>
                {categoryState.isLoading ? (
                    <SubcategoryLinkBarLoader />
                ) : (
                    <SubcategoryLinkBar category={categoryState.category} />
                )}
                <div className="ud-container ud-page-wrapper">
                    <h1
                        className={classNames(
                            effectiveIsMobile ? 'ud-heading-serif-xl' : 'ud-heading-serif-xxl',
                            styles['heading-primary'],
                        )}
                    >
                        {headerText}
                    </h1>
                    {pageObject.type === 'subcategory' ? (
                        <VernacularCohortBrowseBanner
                            page="subcategory"
                            subcategoryId={pageObject.id}
                            parentCategoryId={pageObject.category_id}
                        />
                    ) : (
                        <VernacularCohortBrowseBanner
                            page="category"
                            udemyCategoryId={pageObject.id}
                        />
                    )}
                    <Provider showCodingExercisesBadge={showCodingExercisesBadge}>
                        <ResourceContextMenuProvider>
                            <BrowseCourseBadgesContext.Provider value={browseCourseContextProps}>
                                <AsyncPriceCourseCardContext.Provider
                                    value={asyncPriceBrowseCardContextValue}
                                >
                                    {showThreeCardVerticalLayout ? (
                                        <ContentArea>{discoveryAndDirectorySection}</ContentArea>
                                    ) : (
                                        discoveryAndDirectorySection
                                    )}
                                </AsyncPriceCourseCardContext.Provider>
                            </BrowseCourseBadgesContext.Provider>
                        </ResourceContextMenuProvider>
                    </Provider>
                </div>
            </>
        );
    },
);
